// One trade in the register: two facing "sends" columns, status stamp,
// role-appropriate actions. Pure presentation — callbacks come from TradesPage.
import React from 'react';
import { Trade, TradeItem, TradeStatus } from '../../types';
import { timeAgo } from '../../utils/timeAgo';
import { seasonAverage } from '../../utils/playerSeason';

// Display labels for backend statuses. `accepted` is a transient backend
// state never returned at rest; treat as EXECUTED if ever seen.
export const STATUS_LABELS: Record<TradeStatus, string> = {
  pending: 'PENDING',
  accepted: 'EXECUTED',
  executed: 'EXECUTED',
  rejected: 'DECLINED',
  cancelled: 'WITHDRAWN',
  vetoed: 'VETOED',
};

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-gold/15 text-gold-700 border-gold',
  EXECUTED: 'bg-success/10 text-success border-success',
  DECLINED: 'bg-secondary-100 text-secondary-500 border-secondary-300',
  WITHDRAWN: 'bg-secondary-100 text-secondary-500 border-secondary-300',
  VETOED: 'bg-danger/10 text-danger border-danger',
};

interface TradeCardProps {
  trade: Trade;
  myTeamId: number | null;
  isCommissioner: boolean;
  busy: boolean;
  error?: string | null;
  // When true (the log copy of a trade already shown in a role section), render
  // no action row and no inline error — both are action feedback that belongs on
  // the role-section copy, and a duplicated enabled button could fire a second POST.
  suppressActions?: boolean;
  onAccept: (trade: Trade) => void;
  onReject: (trade: Trade) => void;
  onCancel: (trade: Trade) => void;
  onVeto: (trade: Trade) => void;
}

function PlayerRow({ item }: { item: TradeItem }) {
  const p = item.player;
  if (!p) return null;
  const season = seasonAverage(p);
  return (
    <div className="flex items-center gap-2 py-1.5">
      {p.headshot_url ? (
        <img src={p.headshot_url} alt="" className="w-8 h-8 object-cover bg-secondary-100" />
      ) : (
        <div className="w-8 h-8 bg-secondary-100" />
      )}
      <span className="text-sm font-medium text-ink-900 truncate">{p.name}</span>
      <span
        className={`px-1.5 py-0.5 text-[0.65rem] font-display leading-none ${
          p.position === 'K' ? 'bg-ink-900 text-gold' : 'bg-success text-white'
        }`}
      >
        {p.position}
      </span>
      <span className="ml-auto text-right leading-tight">
        <span className="block font-display text-sm text-secondary-500 tabular-nums">{season.display}</span>
        <span className="block text-[0.55rem] uppercase tracking-wider text-secondary-400">{season.label}</span>
      </span>
    </div>
  );
}

export default function TradeCard({
  trade, myTeamId, isCommissioner, busy, error, suppressActions,
  onAccept, onReject, onCancel, onVeto,
}: TradeCardProps) {
  const label = STATUS_LABELS[trade.status] ?? trade.status.toUpperCase();
  const proposerSends = trade.items.filter((i) => i.from_team_id === trade.proposer_team_id);
  const recipientSends = trade.items.filter((i) => i.from_team_id === trade.recipient_team_id);
  const isPending = trade.status === 'pending';
  const iAmRecipient = myTeamId !== null && trade.recipient_team_id === myTeamId;
  const iAmProposer = myTeamId !== null && trade.proposer_team_id === myTeamId;

  const actionBtn = (label: string, onClick: () => void, variant: 'solid' | 'outline' | 'danger') => (
    <button
      onClick={onClick}
      disabled={busy}
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider border transition-colors disabled:opacity-50 ${
        variant === 'solid'
          ? 'bg-ink-900 text-paper border-ink-900 hover:bg-ink-900/90'
          : variant === 'danger'
          ? 'bg-white text-danger border-danger hover:bg-danger hover:text-white'
          : 'bg-white text-ink-900 border-ink-900 hover:bg-ink-900 hover:text-paper'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="bg-white border border-secondary-200">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-secondary-200">
        <span className="text-xs text-secondary-400">{timeAgo(trade.proposed_at)}</span>
        <span className={`px-2 py-0.5 text-[0.65rem] font-display tracking-wider border ${STATUS_STYLES[label] ?? ''}`}>
          {label}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-secondary-200">
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1">
            {trade.proposer_team?.team_name ?? 'Team'} sends
          </p>
          {proposerSends.map((item) => <PlayerRow key={item.id} item={item} />)}
        </div>
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1">
            {trade.recipient_team?.team_name ?? 'Team'} sends
          </p>
          {recipientSends.map((item) => <PlayerRow key={item.id} item={item} />)}
        </div>
      </div>

      {trade.message && (
        <p className="px-4 py-2 text-sm italic text-secondary-500 border-t border-secondary-200">
          “{trade.message}”
        </p>
      )}

      {!suppressActions && error && (
        <p className="px-4 py-2 text-sm text-danger border-t border-secondary-200">{error}</p>
      )}

      {!suppressActions && isPending && (iAmRecipient || iAmProposer || isCommissioner) && (
        <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-secondary-200">
          {iAmRecipient && actionBtn('Accept', () => onAccept(trade), 'solid')}
          {iAmRecipient && actionBtn('Reject', () => onReject(trade), 'outline')}
          {iAmProposer && actionBtn('Cancel Offer', () => onCancel(trade), 'outline')}
          {isCommissioner && actionBtn('Veto', () => onVeto(trade), 'danger')}
        </div>
      )}
    </div>
  );
}
